<template>
  <div class="position-relative">
    <share-collapse id="share" :entity="feed" :is-feed="true" />
    <basic-portlet title="Feed Information" icon="fa fa-edit">
      <div v-if="feed.has_changes && feed.update_type === 1" class="alert alert-outline-dark">
        <div class="alert-text">
          <p class="font-weight-bold">
            This feed has unpublished changes. These changes will not be added to the Feed RSS <span v-if="feed.type === 'live'">or live feed</span> unless published.
          </p>
          <button class="btn btn-ss-standout" @click="publishChanges(feed)">
            Publish Changes
          </button>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="form-group kt-form__group">
            <label for="feed_uuid">Feed UUID</label>
            <input id="feed_uuid" type="text"
                   disabled
                   class="form-control kt-input" :value="feed.uuid">
          </div>
        </div>
        <!-- Feed Info Left -->
        <div class="col-12 col-md-6">
          <div class="form-group kt-form__group">
            <label>Name <i v-if="feed_name_warning"
                           title="Warning: Changing the name of your feed will change your feed URL. In the case of a podcast, you will need to update your various distributors (Apple Podcasts, Spotify etc.) to the new URL."
                           class="fa fa-exclamation-triangle" /></label>
            <input v-model="feed.name"
                   v-validate="'required'"
                   data-vv-scope="general-feed-form"
                   type="text"
                   name="Feed Name"
                   class="form-control kt-input">
            <p v-show="errors.has('Feed Name', 'general-feed-form')" class="help kt-font-danger">
              {{ errors.first('Feed Name', 'general-feed-form') }}
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label for="feed-description">Description</label>
            <tip-tap v-model="feed.description" input-id="feed-description" />
          </div>

          <div class="form-group kt-form__group">
            <multiselect v-model="feed_type"
                         :options="feed_types"
                         label="label"
                         placeholder="Select a Feed Type..." />
          </div>
          <div class="form-group kt-form__group">
            <label>Update Type</label>
            <div class="kt-radio-inline">
              <label class="kt-radio">
                <input v-model="feed.update_type" type="radio"
                       name="update_instant"
                       :value="0"> Publish changes instantly
                <span />
              </label>
              <label class="kt-radio">
                <input v-model="feed.update_type" type="radio"
                       name="update_manual"
                       :value="1"> Publish changes manually
                <span />
              </label>
            </div>
            <span class="form-text text-muted">
              Instant : Feed RSS and Live Playlists will update as soon as changes are made<br>
              Manual : Feed RSS and Live Feeds will only update when you click the 'Publish Changes' button.
            </span>
          </div>
          <div class="form-group kt-form__group">
            <label>Media URL Prefix</label>
            <input v-model="feed.storage_url_prefix"
                   v-validate="{url: {require_protocol: true }}"
                   data-vv-scope="general-feed-form"
                   type="text"
                   name="Media URL Prefix"
                   class="form-control kt-input">
            <p v-show="errors.has('Media URL Prefix', 'general-feed-form')" class="help kt-font-danger">
              {{ errors.first('Media URL Prefix', 'general-feed-form') }}
            </p>

            <span class="form-text text-muted">
              Prepend a URL to all {{ getItemLabel(true) }}s in your {{ getFeedLabel(true) }}. This is usually used for third party analytics i.e. Podder or Chartable.
            </span>
          </div>
        </div>

        <!-- Feed Info Right -->
        <div class="col-12 col-md-6">
          <div class="row">
            <div class="col-12 col-md-7 col-lg-5 mr-auto">
              <display-with-selection-modal :preselected-artwork="artwork_to_display"
                                            @selected="(ev) => (selected_artwork = ev.selected_artwork)" />
            </div>
          </div>

          <div class="row">
            <div v-if="feed.feed_type.format === 'podcast'" class="col-12 col-md-8 col-lg-8 mr-auto">
              <display-with-selection-modal :preselected-artwork="banner_to_display"
                                            label="Banner Artwork"
                                            banner-suitable-only
                                            lock-suitability
                                            clearable
                                            @selected="ev => {
                                              selected_banner_artwork = ev.selected_artwork;
                                              feed.banner_artwork = null;
                                            }" />
            </div>
          </div>

          <div class="form-group kt-form__group">
            <label>Tags</label>
            <multiselect v-model="ms_tag_values"
                         :options="auto_complete_tags"
                         :multiple="true"
                         :taggable="true"
                         track-by="uuid"
                         label="name"
                         placeholder="Search or add tags..."
                         @tag="addNewTag" />
          </div>

          <div class="form-group kt-form__group">
            <ss-checkbox id="feed-auto-sync-items-tags" v-model="feed.auto_sync_items_by_tags">
              Automatically sync items with matching tags
            </ss-checkbox><br>
            <span class="form-text text-muted">
              Enabling this feature will sync any {{ getItemLabel() }} with any corresponding tag to this feed.<br>
              Please note that if an {{ getItemLabel() }} is manually removed from a feed, it will be re-added if any tags are matched.
            </span>
          </div>

          <div v-perms="['api']" class="form-group kt-form__group">
            <ss-checkbox id="feed-sync-to-website" v-model="feed.sync_to_website">
              Sync to Website
            </ss-checkbox><br>
            <span class="form-text text-muted">
              If you have website integrated with Timbre, this can be used as a flag to determine if this feed should be displayed.<br>
            </span>
          </div>
          <div class="form-group kt-form__group">
            <label style="padding-right: 10px;">{{ getItemLabel(true) }} Ordering</label>
            <radio-group v-model="feed.ordering"
                         group-name="feed_ordering"
                         :options="ordering_types" />
          </div>
        </div>
      </div>
    </basic-portlet>

    <!--begin::Portlet-->
    <items-feed-assign-table :feed="feed" />
    <!--end::Portlet-->

    <!--begin::Portlet-->
    <collapse-portlet v-if="!init"
                      id="feed_channel_portlet"
                      :expanded="false"
                      :locked="show_itunes_detail_create"
                      locked-tooltip="Feed channel needs to be attached first"
                      :always-show-actions="true"
                      title="Feed Channel Information"
                      icon="socion-itunes">
      <template #portlet_actions="props">
        <button v-if="show_itunes_detail_create" class="btn btn-ss"
                @click="() => {
                  props.toggleLock(false);
                  props.toggleExpanded(true);
                  attachItunesDetail();
                }">
          <span>
            <i class="la la-plus-square" />
            <span>Attach Feed Channel Information</span>
          </span>
        </button>
        <button v-if="!show_itunes_detail_create"
                class="btn btn-ss"
                @click="() => {
                  confirmDetachItunesDetail(() => {
                    props.toggleExpanded(false);
                    props.toggleLock(true);
                  });
                }">
          <span>
            <i class="la la-minus-square" />
            <span>Detach Feed Channel Information</span>
          </span>
        </button>
      </template>
      <div v-if="!show_itunes_detail_create && itunes_detail" class="row">
        <!-- Left Side -->
        <div class="col-12 col-md-6">
          <div class="form-group">
            <label>Type</label>
            <div class="kt-radio-inline">
              <label class="kt-radio">
                <input v-model="itunes_detail.type" type="radio"
                       value="episodic"
                       name="feed_type"> Episodic
                <span />
              </label>
              <label class="kt-radio">
                <input v-model="itunes_detail.type" type="radio"
                       value="serial"
                       name="feed_type"> Serial
                <span />
              </label>
            </div>
            <p class="form-text text-muted">
              <strong>Episodic :</strong> Presented and recommended form newest-to-oldest. Will be grouped by seasons if provided.<br>
              <strong>Serial :</strong> For shows with episodes presented and recommended oldest-to-newest and grouped by seasons. Recommended for narrative and story telling formats.
            </p>
          </div>

          <div class="form-group kt-form__group">
            <label for="itunes_summary">Summary</label>
            <tip-tap v-model="itunes_detail.summary" />
            <p v-show="errors.has('Language', 'itunes-detail-form')" class="help kt-font-danger">
              {{ errors.first('Language', 'itunes-detail-form') }}
            </p>
            <p class="form-text text-muted">
              If blank, will default to using the host feed's Description!
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label>Language</label>
            <multiselect v-if="itunes_detail"
                         v-model="itunes_detail.language"
                         v-validate="'required'"
                         data-vv-scope="itunes-detail-form"
                         name="Language"
                         :options="language_codes"
                         track-by="code"
                         :custom-label="languagesCustomLabel"
                         :searchable="true"
                         :show-labels="false"
                         placeholder="Pick a Language" />
            <p v-show="errors.has('Language', 'itunes-detail-form')" class="help kt-font-danger">
              {{ errors.first('Language', 'itunes-detail-form') }}
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label>Copyright</label>
            <input v-model="itunes_detail.copyright" v-validate="'required|min:2|max:100'"
                   name="Copyright" type="text"
                   data-vv-scope="itunes-detail-form"
                   class="form-control kt-input">
            <p v-show="errors.has('Copyright', 'itunes-detail-form')" class="help kt-font-danger">
              {{ errors.first('Copyright', 'itunes-detail-form') }}
            </p>
            <p class="form-text text-muted">
              Example: @ Company Name Ltd. {{ new Date().getFullYear() }}
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label>Managing Editor</label>
            <input id="managing_editor" v-model="itunes_detail.managing_editor"
                   v-validate="'required|email_and_name_in_brackets|min:2|max:100'" data-vv-scope="itunes-detail-form"
                   name="Managing Editor"
                   type="text" class="form-control kt-input">
            <p v-show="errors.has('Managing Editor', 'itunes-detail-form')" class="help  kt-font-danger">
              {{ errors.first('Managing Editor', 'itunes-detail-form') }}
            </p>
            <p class="form-text text-muted">
              Must be an email followed by name in brackets 'john.doe@example.com (John Doe)'
            </p>
          </div>

          <div class="form-group kt-form__group">
            <label>Webmaster</label>
            <input id="webmaster" v-model="itunes_detail.webmaster"
                   v-validate="'required|email_and_name_in_brackets|min:2|max:100'" data-vv-scope="itunes-detail-form"
                   type="text"
                   name="Webmaster"
                   class="form-control kt-input">
            <p v-show="errors.has('Webmaster', 'itunes-detail-form')" class="help  kt-font-danger">
              {{ errors.first('Webmaster', 'itunes-detail-form') }}
            </p>
            <p class="form-text text-muted">
              Must be an email followed by name in brackets 'john.doe@example.com (John Doe)'
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label for="itunes_detail_publish_date">Publish
              Date</label>
            <datetime id="itunes_detail_publish_date"
                      v-model="itunes_detail.publish_date" v-validate="'required'"
                      data-vv-scope="itunes-detail-form"
                      name="Publish Date" input-class="form-control kt-input"
                      type="datetime"
                      format="yyyy-MM-dd HH:mm:ss" />
            <p v-show="errors.has('Publish Date', 'itunes-detail-form')" class="help kt-font-danger">
              {{ errors.first('Publish Date', 'itunes-detail-form') }}
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label>Category</label>
            <input v-model="itunes_detail.itunes_category_id"
                   type="hidden">
            <multiselect v-model="selected_itunes_category"
                         :options="itunes_categories"
                         track-by="id"
                         :searchable="true"
                         :show-labels="false"
                         label="name"
                         placeholder="Pick a value"
                         @input="setItunesSubcategories">
              <template #singleLabel="props">
                {{ props.option.name }} <span v-if="props.option.type === 2" class="legacy-label">legacy</span>
              </template>
              <template #option="props">
                {{ props.option.name }} <span v-if="props.option.type === 2" class="legacy-label">legacy</span>
              </template>
            </multiselect>
            <p class="form-text text-muted">
              Category is required for feed to work properly. List contains both legacy and current values.
            </p>
          </div>

          <div v-show="itunes_subcategories_filtered.length > 0 && selected_itunes_category !== null"
               class="form-group kt-form__group">
            <label>Subcategory</label>
            <input v-model="itunes_detail.itunes_subcategory_id" type="hidden">
            <multiselect v-model="selected_itunes_subcategory"
                         :options="itunes_subcategories_filtered"
                         track-by="id"
                         :searchable="true"
                         :show-labels="false"
                         label="name"
                         placeholder="Pick a value">
              <template #singleLabel="props">
                {{ props.option.name }} <span v-if="props.option.type === 2"
                                              class="legacy-label">legacy</span>
              </template>
              <template #option="props">
                {{ props.option.name }} <span v-if="props.option.type === 2"
                                              class="legacy-label">legacy</span>
              </template>
            </multiselect>
            <p class="form-text text-muted">
              Subcategory is optional but recommended.
            </p>
          </div>

          <div class="form-group kt-form__group">
            <label>Author</label>
            <input v-model="itunes_detail.author" type="text"
                   class="form-control kt-input">
            <p class="form-text text-muted">
              i.e. Artist or Presenter. If not populated, Managing Editor will be used in its place.
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label for="explicit">Explicit</label>
            <select id="explicit" v-model="itunes_detail.explicit"
                    v-validate="'required'" data-vv-scope="itunes-detail-form"
                    class="form-control kt-input"
                    name="Episode Explicitly" :v-validate="'required'">
              <option v-for="(option, index) in explicit_options" :key="index" :value="option">
                {{ option | capitalize }}
              </option>
            </select>
            <p class="form-text text-muted">
              Does this feed contain any explicit content?
            </p>
          </div>

          <div class="form-group kt-form__group">
            <label>Website Link</label>
            <input v-model="itunes_detail.link" v-validate="{required: false, url: {require_protocol: true }}"
                   name="Website Link" type="text"
                   data-vv-scope="itunes-detail-form"
                   class="form-control kt-input">
            <p v-show="errors.has('Website Link', 'itunes-detail-form')" class="help kt-font-danger">
              {{ errors.first('Website Link', 'itunes-detail-form') }}
            </p>
            <p class="form-text text-muted">
              Used in podcast providers to provide a web link to your feed. This can be the link to your website, apple podcasts, etc.<br>
              This will fallback to the Timbre Player URL.<br>
              Ensure that the webpage has the appropriate RSS feed link in the <code>&lt;head&gt;</code> section of the page e.g. <code>&lt;link rel="alternate" type="application/rss+xml" title="{{ feed.name }}" href="{{ feed.feed_xml_file_url }}" /&gt;</code>
            </p>
          </div>

          <div class="form-group kt-form__group">
            <label>New Feed Url</label>
            <input v-model="itunes_detail.new_feed_url" v-validate="{required: false, url: {require_protocol: true }}"
                   name="New Feed Url" type="text"
                   data-vv-scope="itunes-detail-form"
                   class="form-control kt-input">
            <p v-show="errors.has('New Feed Url', 'itunes-detail-form')" class="help kt-font-danger">
              {{ errors.first('New Feed Url', 'itunes-detail-form') }}
            </p>
            <p class="form-text text-muted">
              Used when migrating your feed to a new url. This will act as a redirect.
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label>Owner Name</label>
            <input v-model="itunes_detail.owner_name" type="text"
                   class="form-control kt-input">
            <p class="form-text text-muted">
              Contact Name of the Feed/Podcast Owner
            </p>
          </div>
          <div class="form-group kt-form__group">
            <label>Owner Email</label>
            <input v-model="itunes_detail.owner_email" v-validate="'email'"
                   name="Owner Email" type="text"
                   data-vv-scope="itunes-detail-form"
                   class="form-control kt-input">
            <p v-show="errors.has('Owner Email', 'itunes-detail-form')" class="help kt-font-danger">
              {{ errors.first('Owner Email', 'itunes-detail-form') }}
            </p>
            <p class="form-text text-muted">
              Contact Email of the Feed/Podcast Owner
            </p>
          </div>
        </div>

        <!-- Right Side -->
        <div class="col-12 col-md-6">
          <div class="form-group kt-form__group">
            <label>Image Url</label>
            <input v-model="itunes_detail.image_url" v-validate="{url: {require_protocol: true }}"
                   name="Image Url"
                   type="text" data-vv-scope="itunes-detail-form"
                   class="form-control kt-input">
            <p v-show="errors.has('Image Url', 'itunes-detail-form')" class="help kt-font-danger">
              {{ errors.first('Image Url', 'itunes-detail-form') }}
            </p>
            <p class="form-text text-muted">
              Used if no selected artwork. Valid URL with protocol (http:// or https://)
            </p>
          </div>
        </div>
      </div>
    </collapse-portlet>
    <!--end::Portlet-->

    <!--begin::Portlet-->
    <collapse-portlet title="External Links" icon="la la-link"
                      :expanded="false">
      <external-links v-model="feed.external_links" :feed="feed" />
    </collapse-portlet>
    <!--end::Portlet-->

    <sticky-control-bar>
      <button class="btn btn-ss-standout"
              :disabled="updating"
              @click="updateFeed()">
        <loader v-if="updating" small class="mr-2" />
        <i v-else class="la la-save" />
        <span>Update</span>
      </button>

      <button class="btn btn-ss-standout"
              :disabled="updating"
              @click="() => initQuickCreateModal(feed)">
        <i class="flaticon2-plus pr-2" />Assign New Item
      </button>
      <button v-if="feed.has_changes && feed.update_type === 1"
              :disabled="updating"
              class="btn btn-ss-standout"
              @click="publishChanges(feed)">
        Publish Changes
      </button>
      <b-dropdown text="Actions" :disabled="updating" class="btn-ss">
        <b-dropdown-item :href="`${feed.feed_xml_file_url}`" target="_blank">
          <i class="la la-eye" />Feed RSS
        </b-dropdown-item>
        <b-dropdown-item-button @click="callbackAndRedirect(() => duplicateFeeds(feed))">
          <i class="la la-copy" />Duplicate
        </b-dropdown-item-button>
        <b-dropdown-item-button @click="callbackAndReloadTable(() => confirmSyncFeedItemsByTags(feed))">
          <i class="la la-tags" />Sync {{ getItemLabel(true) }}s by Feed Tags
        </b-dropdown-item-button>
        <b-dropdown-item-button @click="callbackAndReloadTable(() => confirmSyncFeedTagsToFeedItems(feed))">
          <i class="la la-tags" />Sync Feed Tags to {{ getItemLabel(true) }}s
        </b-dropdown-item-button>
        <b-dropdown-divider />
        <b-dropdown-item-button variant="ss-danger" @click="callbackAndRedirect(() => destroyFeeds(feed), '/feeds')">
          <i class="flaticon2-warning" />Delete
        </b-dropdown-item-button>
      </b-dropdown>
    </sticky-control-bar>

    <alert-modal name="detach-modal"
                 style-variant="warning"
                 header="Warning:"
                 margin-x="30vw">
      <h4 class="text-center mb-4">
        Channel information will be lost if detached.
      </h4>
    </alert-modal>

    <alert-modal name="edit-feed-name"
                 style-variant="warning"
                 header="Warning:"
                 margin-x="30vw">
      <h4 class="text-center mb-4">
        Changing the name of your feed will change the feed URL. In the case of a podcast, you will need to update your various distributors (Apple Podcasts, Spotify etc.) to the new URL.
      </h4>
    </alert-modal>

    <sync-feed-tags-to-items-modal name="sync-tags-items" :feeds="[feed]" />
    <sync-items-to-feed-by-tag-modal name="sync-items-tags" :feeds="[feed]" />
    <item-creation-modal :preselected-feed="feed" />
  </div>
</template>

<script>
import {
  BDropdown,
  BDropdownItem,
  BDropdownItemButton,
  BDropdownDivider,
} from 'bootstrap-vue';
import { HandleDirective } from "vue-slicksort";
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";
import ShareCollapse from "VuePartials/App/Item/ShareCollapse";
import VeeValidate from 'vee-validate';
import {
  mysqlDateTimeUTCToLocal,
} from 'Helpers/formatters';
import CollapsePortlet from 'VueComponents/CollapsePortlet';
import SocialShareMixin from 'VueMixins/SocialShareMixin';
import FeedCreateEditMixin from "VueMixins/Feeds/FeedCreateEditMixin";
import ItemsFeedAssignTable from 'VuePartials/ItemsFeedAssignTable';
import FeedsApiMixin from "VueMixins/Feeds/FeedsApiMixin";
import FeedsModalsMixin from 'VueMixins/Feeds/FeedsModalsMixin';
import SyncItemsToFeedByTagModal from 'VueModals/SyncItemsToFeedByTagModal';
import SyncFeedTagsToItemsModal from 'VueModals/SyncFeedTagsToItemsModal';
import { splitOnFirst } from 'Lib/string-util';
import DisplayWithSelectionModal from "VuePartials/Artwork/DisplayWithSelectionModal";
import ExternalLinks from 'VueApp/Feeds/ExternalLinks';
import AlertModal from 'VueModals/AlertModal';
import BasicPortlet from "VueComponents/BasicPortlet";
import RadioGroup from "VueComponents/RadioGroup";
import StickyControlBar from 'VueComponents/StickyControlBar';
import ItemQuickCreateMixin from 'VueMixins/ItemQuickCreateMixin';
import TaggableModelMixin from 'VueMixins/TaggableModelMixin';

Vue.use(VeeValidate);
// Validates that a given string has some text then an email address within brackets
VeeValidate.Validator.extend('email_and_name_in_brackets', {
  getMessage: () =>
    `Must be an email followed by name in brackets 'john.doe@example.com (John Doe)'`,
  validate: (text) => {
    const [email, name] = splitOnFirst(text, ` `);
    const valid_email = /\S+@\S+\.\S+/g.test(email);
    const valid_name = /^\(((?:[\w\s]+))\)$/g.test(name);
    return valid_email && valid_name;
  },
});

export default {
  directives: { handle: HandleDirective },
  components: {
    BasicPortlet,
    DisplayWithSelectionModal,
    Datetime,
    CollapsePortlet,
    ShareCollapse,
    ItemsFeedAssignTable,
    BDropdown,
    BDropdownItem,
    BDropdownItemButton,
    BDropdownDivider,
    SyncItemsToFeedByTagModal,
    SyncFeedTagsToItemsModal,
    ExternalLinks,
    AlertModal,
    RadioGroup,
    StickyControlBar,
  },
  filters: {
    capitalize: function (value) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  mixins: [
    SocialShareMixin,
    FeedCreateEditMixin,
    FeedsApiMixin,
    FeedsModalsMixin,
    ItemQuickCreateMixin,
    TaggableModelMixin,
  ],
  data: () => ({
    updating: false,
    user: window.user,
    tag: "",
    tags: [],
    items: [],
    auto_complete_tags: [],
    feed: window.feed,
    current_feed_name: window.feed.name,
    feed_name_warning: false,
    itunes_detail: null,
    explicit_options: ['yes', 'clean'],
    itunes_categories: [],
    itunes_subcategories: [],
    itunes_subcategories_filtered: [],
    selected_itunes_category: null,
    selected_itunes_subcategory: null,
    addItemsClicked: false,
    show_itunes_detail_create: false,
    artwork: [],
    language_codes: window.language_codes,
    init: true,
    ordering_types: [
      {
        display: 'Newest Added',
        value: 1,
      },
      {
        display: 'Oldest Added',
        value: 2,
      },
      {
        display: 'Published Newest',
        value: 3,
      },
      {
        display: 'Published Oldest',
        value: 4,
      },
      {
        display: 'Random',
        value: 6,
      },
    ],
  }),
  computed: {
    unselectedItems() {
      const vm = this;
      if (!this.items || !this.items.length) {
        return [];
      }

      return _.filter(this.items, (item) => {
        const item_found = _.find(vm.feed.items, feed_item => feed_item.uuid === item.uuid);
        return !item_found;
      });
    },
  },
  watch: {
    feed: {
      handler(val) {
        if (val.name === this.current_feed_name) {
          this.feed_name_warning = false;
        } else {
          this.feed_name_warning = true;
        }
      },
      deep: true,
    },
    itunesCategory: function (value) {
      if (value !== undefined) {
        this.itunes_detail.itunes_category_id = value.id;
      } else {
        this.itunes_detail.itunes_category_id = this.itunes_categories[0]["id"];
      }
    },
    itunesSubcategory: function (value) {
      if (value !== undefined) {
        this.itunes_detail.itunes_subcategory_id = value.id;
      } else {
        this.itunes_detail.itunes_subcategory_id = null;
      }
    },
  },
  beforeMount() {
    const ordering_option = _.find(this.ordering_types, {
      'value': this.feed.ordering,
    });
    if (ordering_option) {
      ordering_option.checked = true;
    } else {
      this.ordering_types[0].checked = true;
    }
  },
  mounted() {
    this.ms_tag_values = this.feed.tags;
    this.fetchTags();
    this.getFeedTypes();

    if (this.feed.itunes_detail === null) {
      this.show_itunes_detail_create = true;
    } else {
      this.setItunesDetails();
    }

    if (this.itunes_detail) {
      this.getItunesCategories();
      this.getItunesSubcategories();
    }

    this.init = false;

    EventBus.$on('feeds_updated', (payload) => {
      _.forEach(payload, (response) => {
        if (response.data?.uuid === this.feed.uuid) {
          this.feed = response.data;
        }
      });
    });

    EventBus.$on("modal-complete", (ev) => {
      if (ev.name === "item-create-modal") {
        this.reloadTable();
      }
    });
  },
  methods: {
    reloadTable() {
      window.EventBus.$emit(`item-feed-table-reload`);
    },
    async callbackAndReloadTable(callback) {
      this.updating = true;
      try {
        await callback();
        this.reloadTable();
      } catch (err) {
        toastr.error(err);
        console.error(err);
      }
      this.updating = false;
    },
    async callbackAndRedirect(callback, redirect_url) {
      this.updating = true;
      try {
        const result = await callback();
        if (!redirect_url && result.length && result[0].data) {
          redirect_url = `/feeds/${result[0].data.uuid}/edit`;
        }

        const a_el = document.createElement('a');
        a_el.href = redirect_url;
        document.querySelector('body').appendChild(a_el);
        a_el.click();
      } catch (err) {
        toastr.error(err);
        console.error(err);
      }
      this.updating = false;
    },

    getPublishDate(date) {
      if (date) {
        return mysqlDateTimeUTCToLocal(date, "DD/MM/YYYY HH:mm:ss");
      }

      return "Not Set";
    },
    setItunesDetails() {
      const vm = this;
      // Create a non-reactive copy of the itunes_detail
      this.itunes_detail = JSON.parse(JSON.stringify(this.feed.itunes_detail));
      // Select the corresponding language object form language_codes array
      if (this.itunes_detail.language) {
        const selected_language = _.find(this.language_codes, language => language.code === vm.feed.itunes_detail.language);
        this.itunes_detail.language = selected_language;
      }
      if (this.itunes_detail.publish_date) {
        this.itunes_detail.publish_date = moment(
          this.itunes_detail.publish_date,
        ).toISOString();
      }

      if (!this.itunes_detail.explicit) {
        this.itunes_detail.explicit = 'clean';
      }
    },
    addItems() {
      this.addItemsClicked = true;
      this.getItems();
    },
    async getItems() {
      try {
        const response = await axios.get(`/api/v1/items`);
        this.items = response.data.data;
      } catch (error) {
        toastr.error("Oops. Something went wrong when retrieving feed items");
      }
    },
    async deleteAndRedirectToIndex() {
      await this.confirmDestroyFeeds(this.feed);
      window.location.href = `/feeds`;
    },
    async getItunesCategories() {
      const vm = this;
      try {
        const response = await axios.get(`/api/v1/itunes/categories`);
        this.itunes_categories = response.data.data;

        if (this.itunes_detail && this.itunes_detail.itunes_category_id) {
          this.selected_itunes_category = _.find(this.itunes_categories, category => category.id === vm.itunes_detail.itunes_category_id);
        }
      } catch (error) {
        toastr.error("Oops. Something went wrong when retrieving itunes categories");
      }
    },

    async getItunesSubcategories() {
      const vm = this;
      try {
        const response = await axios.get(`/api/v1/itunes/subcategories`);
        this.itunes_subcategories = response.data.data;

        if (this.itunes_detail) {
          if (this.itunes_detail.itunes_subcategory_id) {
            this.selected_itunes_subcategory = _.find(this.itunes_subcategories, subcategory => subcategory.id === vm.itunes_detail.itunes_subcategory_id);
          }
          // fill the list of subcategories based on the current selected id
          this.itunes_subcategories_filtered = _.filter(this.itunes_subcategories, {
            'itunes_category_id': this.itunes_detail.itunes_category_id,
          });
        }
      } catch (error) {
        toastr.error("Oops. Something went wrong when retrieving subcategories");
      }
    },

    async attachItunesDetail() {
      const vm = this;
      try {
        KTApp.blockPage();
        const response = await axios.post(`/api/v1/feeds/${vm.feed.uuid}/itunes/details`);
        if (response.data.success) {
          this.itunes_detail = response.data.data.itunes_detail;
        }
        this.show_itunes_detail_create = false;
        this.getItunesCategories();
        this.getItunesSubcategories();
      } catch (error) {
        toastr.error(
          "Failed to create podcast channel. Please try again later!",
        );
        this.show_itunes_detail_create = true;
        this.itunes_detail = { explicit: 'clean' };
      }
      KTApp.unblockPage();
    },
    async confirmDetachItunesDetail(callback) {
      const res = await this.fireModal(`detach-modal`);
      if (!res.data.confirm) return;
      toastr.success(`Detached channel information`);
      await callback();
      this.detachItunesDetail();
    },
    async detachItunesDetail() {
      try {
        const response = await axios.delete(
          `/api/v1/feeds/${this.feed.uuid}/itunes/details`,
        );
        this.show_itunes_detail_create = true;
        const vm = this;
        // Nuke the validator
        this.$validator.pause();
        this.$nextTick(() => {
          vm.itunes_detail = null;
          this.$validator.errors.clear();
          this.$validator.fields.items.forEach(field => field.reset());
          this.$validator.fields.items.forEach(field => this.errors.remove(field));
          this.$validator.resume();
          window.EventBus.$emit(`feed-updated`, response.data.data);
        });
      } catch (error) {
        toastr.error(
          "Failed to remove Channel Information. Please try again later!",
        );
        this.show_itunes_detail_create = false;
      }
    },

    async updateFeed() {
      if (this.current_feed_name !== this.feed.name) {
        const res = await this.fireModal('edit-feed-name');

        if (!res.data.confirm) return;
      }

      // Validate the itunes details using v-validate
      try {
        const valid = await this.$validator.validateAll('feed-general-form');

        if (!valid) {
          toastr.error("Some fields have not been populated correctly.");
          return;
        }
      } catch (error) {
        console.error(error);
      }

      if (this.itunes_detail) {
        // Validate the itunes details using v-validate
        try {
          const valid = await this.$validator.validateAll('itunes-detail-form');

          if (!valid) {
            toastr.error("Your channel data is incorrect. Please Review your entries.");
            return;
          }
        } catch (error) {
          console.error(error);
        }
      }

      let itunes_data = null;
      if (this.itunes_detail) {
        try {
          // Create a non-reactive copy of itunes data so we can mutate for post without effecting original
          itunes_data = JSON.parse(JSON.stringify(this.itunes_detail));
          itunes_data.language = this.itunes_detail.language.code;
          itunes_data.itunes_category_id = this.selected_itunes_category.id;
          if (this.selected_itunes_subcategory) {
            itunes_data.itunes_subcategory_id = this.selected_itunes_subcategory.id;
          } else {
            itunes_data.itunes_subcategory_id = null;
          }
          itunes_data.summary = itunes_data.summary ? itunes_data.summary : this.feed.description;
        } catch (error) {
          toastr.error("Your channel information is incorrect, please review it and try again.");
          return;
        }
      }

      this.updating = true;
      try {
        const artwork_uuid = this.selected_artwork?.uuid ?? this.feed.artwork?.uuid ?? null;
        const banner_uuid = this.selected_banner_artwork?.uuid ?? this.feed.banner_artwork?.uuid ?? null;

        const response = await axios.patch(
          `/api/v1/feeds/${this.feed.uuid}`,
          {
            feed_name: this.feed.name,
            feed_update_type: this.feed.update_type,
            feed_type_id: this.feed_type.id,
            feed_description: this.feed.description,
            feed_ordering: this.feed.ordering,
            itunes_detail: itunes_data,
            tags: this.ms_tag_values,
            feed_artwork_uuid: artwork_uuid,
            feed_banner_artwork_uuid: banner_uuid,
            external_links: this.feed.external_links ?? null,
            storage_url_prefix: this.feed.storage_url_prefix ?? null,
            auto_sync_items_by_tags: this.feed.auto_sync_items_by_tags,
            sync_to_website: this.feed.sync_to_website,
          },
        );
        if (response.data.success) {
          toastr.success("Feed has been updated!");
          this.feed = response.data.data;
          window.EventBus.$emit(`feed-updated`, response.data.data);
        } else {
          toastr.error("Feed update failed!");
        }
      } catch (error) {
        window.handleAxiosError(error);
      }
      this.updating = false;
    },
    async addItemToFeed(item) {
      this.feed.items.push(item);
    },
    async removeItemFromFeed(index) {
      this.feed.items.splice(index, 1);
    },
    itemsCustomLabel({ name, description, file_name }) {
      return `${name} – ${description} - ${file_name}`;
    },
    languagesCustomLabel({ name, native_name }) {
      return `${name} ( ${native_name} )`;
    },
    setItunesSubcategories(value) {
      this.selected_itunes_subcategory = null;
      if (value) {
        this.itunes_subcategories_filtered = _.filter(this.itunes_subcategories, {
          'itunes_category_id': value.id,
        });
      }
    },
  },
};
</script>

<style lang='scss' scoped>
@use "sass:map";

@use "~AssetsSass/_variables.scss" as *;
@use "~AssetsSass/_mixins.scss" as *;
@use "~AssetsSass/components/vue-slicksort";
@use "~pretty-checkbox/src/pretty-checkbox.scss";

@include metronic-like-vti;

.image-selection {
  border: 1px solid #ebedf2;
  padding: 20px;
  margin-bottom: 1em;
}

.items-controls {
  margin-bottom: 20px;
  .items-multiselect {
    .multiselect__option {
      overflow-x: hidden;
    }
    .multiselect__option--highlight {
      background: map.get($ss, 'secondary') !important;
    }
    .option__desc, .option__image {
      display: inline-block;
      vertical-align: middle;
    }
    .option__image {
      max-height: 80px;
      margin-right: 10px;
    }
  }

  .multiselect__option--highlight {
    background: map.get($ss, 'secondary') !important;
    &:after {
      background: map.get($ss, 'secondary') !important;
    }
  }

  .item-select-item {
    .item-select-details {
      .item-name {
        font-weight: bold;
        margin-bottom: 15px;
      }
    }
  }
}

.item-description {
  margin-top: 1em;

  .item-name {
    color: map.get($ss-theme, 'hunter-grey');
  }

  .item-details {
    margin: 0;
    margin-bottom: 20px;
  }
}

@media (min-width: 768px) {
  .item-description {
    margin-top: 0;
  }
}

.legacy-label {
  font-size: xx-small;
  padding: 2px;
  color: #000;
}
</style>
